import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReportNumberingSection } from './ReportNumberingSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faBuilding,
  faUpload,
  faGlobe,
  faPhone,
  faEnvelope,
  faClock,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { companyProfileService, BUSINESS_TYPES } from '@/services/company-profile.service';
import {
  tenantSettingsService,
  type UpsertTenantSettingsPayload,
} from '@/services/tenant-settings.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';

function extractApiMessage(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

const TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Australia/Sydney',
  'Pacific/Auckland',
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'ja', label: 'Japanese' },
];

const CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'INR', label: 'INR — Indian Rupee' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'AED', label: 'AED — UAE Dirham' },
  { value: 'SGD', label: 'SGD — Singapore Dollar' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
  { value: 'JPY', label: 'JPY — Japanese Yen' },
];

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2026)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-12-31)' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (31-12-2026)' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY (31 Dec 2026)' },
];

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

// ── Sub-components ────────────────────────────────────────────────────────────

interface SectionCardProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}

function SectionCard({ title, description, children, action }: SectionCardProps) {
  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {description && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{description}</p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className="px-6 py-5">{children}</div>
    </section>
  );
}

interface ComingSoonFieldProps {
  label: string;
  placeholder: string;
}

function ComingSoonField({ label, placeholder }: ComingSoonFieldProps) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
          Soon
        </span>
      </div>
      <Input
        disabled
        placeholder={placeholder}
        className="cursor-not-allowed opacity-50"
        aria-label={label}
      />
    </div>
  );
}

interface SaveButtonProps {
  saving: boolean;
  label?: string;
  savingLabel?: string;
}

function SaveButton({ saving, label = 'Save', savingLabel = 'Saving…' }: SaveButtonProps) {
  return (
    <Button type="submit" size="sm" disabled={saving} className="min-w-[100px]">
      {saving ? (
        <>
          <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
          {savingLabel}
        </>
      ) : (
        label
      )}
    </Button>
  );
}

interface BrandingUploadFieldProps {
  label: string;
  savedUrl: string | null;
  pendingPreviewUrl: string | null;
  hasPending: boolean;
  onSelectFile: (file: File) => void;
  onClearPending: () => void;
  accept: string;
  formats: string;
  dimensions: string;
  previewClass: string;
  disabled: boolean;
}

function BrandingUploadField({
  label,
  savedUrl,
  pendingPreviewUrl,
  hasPending,
  onSelectFile,
  onClearPending,
  accept,
  formats,
  dimensions,
  previewClass,
  disabled,
}: BrandingUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const displayUrl = pendingPreviewUrl ?? savedUrl;
  const hasImage = !!displayUrl;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onSelectFile(file);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        {hasPending && (
          <span className="rounded-full bg-accent/20 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-foreground/70">
            Pending
          </span>
        )}
      </div>

      {/* Preview box */}
      <div
        className={`${previewClass} flex shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed ${
          hasPending ? 'border-primary/40 bg-primary/5' : 'border-border bg-muted/20'
        }`}
      >
        {displayUrl ? (
          <img src={displayUrl} alt={label} className="h-full w-full object-contain p-2" />
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-muted-foreground/40">
            <FontAwesomeIcon icon={faBuilding} className="h-5 w-5" />
            <span className="text-[10px]">No image</span>
          </div>
        )}
      </div>

      {/* Hints */}
      <div className="space-y-0.5">
        <p className="text-xs text-muted-foreground">{formats}</p>
        <p className="text-xs text-muted-foreground">{dimensions}</p>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleChange}
          aria-label={`Select ${label}`}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="gap-2"
        >
          <FontAwesomeIcon icon={faUpload} className="h-3.5 w-3.5" />
          {hasImage ? 'Replace' : 'Choose file'}
        </Button>
        {hasPending && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onClearPending}
            className="gap-1.5 text-muted-foreground"
          >
            <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function CompanyProfilePage() {
  const queryClient = useQueryClient();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['company-profile'],
    queryFn: companyProfileService.get,
    retry: (failureCount, error: Error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) return false;
      return failureCount < 2;
    },
  });

  const { data: localeSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: tenantSettingsService.get,
  });

  // ── Editable company fields ──────────────────────────────────────────────────
  const [companyTitle, setCompanyTitle] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState<string[]>([]);

  // ── Support / address fields ─────────────────────────────────────────────────
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [workingHours, setWorkingHours] = useState('');
  const [supportHours, setSupportHours] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');

  // ── Legal identity — tenant-owned and editable since COMPANIES_DESIGN v1.1 ──
  const [registeredName, setRegisteredName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');

  // ── Branding — deferred upload state ────────────────────────────────────────
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [pendingLogoPreview, setPendingLogoPreview] = useState<string | null>(null);
  const [pendingFaviconFile, setPendingFaviconFile] = useState<File | null>(null);
  const [pendingFaviconPreview, setPendingFaviconPreview] = useState<string | null>(null);

  // Revoke object URLs when they change or component unmounts
  useEffect(() => {
    if (!pendingLogoPreview) return;
    return () => URL.revokeObjectURL(pendingLogoPreview);
  }, [pendingLogoPreview]);

  useEffect(() => {
    if (!pendingFaviconPreview) return;
    return () => URL.revokeObjectURL(pendingFaviconPreview);
  }, [pendingFaviconPreview]);

  // ── Regional state ───────────────────────────────────────────────────────────
  const [timezone, setTimezone] = useState('UTC');
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState('USD');
  const [dateFormat, setDateFormat] = useState('YYYY-MM-DD');
  const [timeFormat, setTimeFormat] = useState('24h');

  // ── Numbering state (Issue 8) ────────────────────────────────────────────────
  const [customerCodePrefix, setCustomerCodePrefix] = useState('CUS');
  const [assetNumberPrefix, setAssetNumberPrefix] = useState('');

  // Populate from API data
  useEffect(() => {
    if (profile) {
      setRegisteredName(profile.registeredName ?? '');
      setRegistrationNumber(profile.registrationNumber ?? '');
      setCompanyTitle(profile.companyTitle ?? '');
      setTaxNumber(profile.taxNumber ?? '');
      setPhone(profile.phone ?? '');
      setWebsite(profile.website ?? '');
      setSupportEmail(profile.supportEmail ?? '');
      setSupportPhone(profile.supportPhone ?? '');
      setWorkingHours(profile.workingHours ?? '');
      setSupportHours(profile.supportHours ?? '');
      setAddressLine1(profile.addressLine1 ?? '');
      setAddressLine2(profile.addressLine2 ?? '');
      setCity(profile.city ?? '');
      setStateVal(profile.state ?? '');
      setCountry(profile.country ?? '');
      setPostalCode(profile.postalCode ?? '');
      setSelectedBusinessTypes(profile.businessTypes);
    }
  }, [profile]);

  useEffect(() => {
    if (localeSettings) {
      setTimezone(localeSettings.timezone);
      setLanguage(localeSettings.language);
      setCurrency(localeSettings.currency);
      setDateFormat(localeSettings.dateFormat);
      setTimeFormat(localeSettings.timeFormat);
      setCustomerCodePrefix(localeSettings.customerCodePrefix);
      setAssetNumberPrefix(localeSettings.assetNumberPrefix);
    }
  }, [localeSettings]);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const profileMutation = useMutation({
    mutationFn: companyProfileService.upsert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company-profile'] });
    },
    onError: (error: Error) => {
      toast.error(extractApiMessage(error, 'Failed to save company information'));
    },
  });

  const localeMutation = useMutation({
    mutationFn: tenantSettingsService.upsert,
    onSuccess: () => {
      toast.success('Regional settings saved');
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error: Error) => {
      toast.error(extractApiMessage(error, 'Failed to save regional settings'));
    },
  });

  const numberingMutation = useMutation({
    mutationFn: tenantSettingsService.upsert,
    onSuccess: () => {
      toast.success('Numbering settings saved');
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error: Error) => {
      toast.error(extractApiMessage(error, 'Failed to save numbering settings'));
    },
  });

  const logoMutation = useMutation({
    mutationFn: companyProfileService.uploadLogo,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company-profile'] });
    },
    onError: (error: Error) => {
      toast.error(extractApiMessage(error, 'Logo upload failed'));
    },
  });

  const faviconMutation = useMutation({
    mutationFn: companyProfileService.uploadFavicon,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company-profile'] });
    },
    onError: (error: Error) => {
      toast.error(extractApiMessage(error, 'Favicon upload failed'));
    },
  });

  // ── Branding helpers ─────────────────────────────────────────────────────────
  const selectLogo = (file: File) => {
    if (pendingLogoPreview) URL.revokeObjectURL(pendingLogoPreview);
    setPendingLogoFile(file);
    setPendingLogoPreview(URL.createObjectURL(file));
  };

  const clearPendingLogo = () => {
    if (pendingLogoPreview) URL.revokeObjectURL(pendingLogoPreview);
    setPendingLogoFile(null);
    setPendingLogoPreview(null);
  };

  const selectFavicon = (file: File) => {
    if (pendingFaviconPreview) URL.revokeObjectURL(pendingFaviconPreview);
    setPendingFaviconFile(file);
    setPendingFaviconPreview(URL.createObjectURL(file));
  };

  const clearPendingFavicon = () => {
    if (pendingFaviconPreview) URL.revokeObjectURL(pendingFaviconPreview);
    setPendingFaviconFile(null);
    setPendingFaviconPreview(null);
  };

  const toggleBusinessType = (value: string) => {
    setSelectedBusinessTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  // ── Save handlers ────────────────────────────────────────────────────────────
  // Each card owns a form and submits only its own fields. The PATCH endpoint
  // applies just the keys present in the body, so a card can never overwrite a
  // sibling's values with whatever happens to be in local state — which is what
  // a single shared form did previously.
  //
  // `savingSection` drives the in-flight label on the one button that was
  // pressed; a shared `isPending` would spin every card's button at once.
  const [savingSection, setSavingSection] = useState<
    'company' | 'support' | 'classification' | 'branding' | null
  >(null);

  const isSavingAll =
    profileMutation.isPending || logoMutation.isPending || faviconMutation.isPending;

  const handleSaveCompanyInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSection('company');
    try {
      // COMPANIES_DESIGN v1.1 — legal identity is tenant-owned and is saved from
      // here. Sent as '' rather than undefined when cleared: the API treats an
      // empty string as CLEAR and an absent key as LEAVE UNCHANGED (OD-C), so
      // `|| undefined` would make it impossible to erase a value.
      await profileMutation.mutateAsync({
        registeredName,
        registrationNumber,
        companyTitle: companyTitle || undefined,
        taxNumber: taxNumber || undefined,
        phone: phone || undefined,
        website: website || undefined,
        workingHours: workingHours || undefined,
      });
      toast.success('Company information saved');
    } catch {
      // mutation onError shows the specific error toast
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveSupportInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSection('support');
    try {
      await profileMutation.mutateAsync({
        supportEmail: supportEmail || undefined,
        supportPhone: supportPhone || undefined,
        supportHours: supportHours || undefined,
        addressLine1: addressLine1 || undefined,
        addressLine2: addressLine2 || undefined,
        city: city || undefined,
        state: stateVal || undefined,
        country: country || undefined,
        postalCode: postalCode || undefined,
      });
      toast.success('Support information saved');
    } catch {
      // mutation onError shows the specific error toast
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveClassification = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSection('classification');
    try {
      await profileMutation.mutateAsync({ businessTypes: selectedBusinessTypes });
      toast.success('Business classification saved');
    } catch {
      // mutation onError shows the specific error toast
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveBranding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingLogoFile && !pendingFaviconFile) {
      toast.error('Choose a logo or favicon first');
      return;
    }
    setSavingSection('branding');
    try {
      let filesUploaded = 0;
      if (pendingLogoFile) {
        await logoMutation.mutateAsync(pendingLogoFile);
        clearPendingLogo();
        filesUploaded++;
      }
      if (pendingFaviconFile) {
        await faviconMutation.mutateAsync(pendingFaviconFile);
        clearPendingFavicon();
        filesUploaded++;
      }
      toast.success(`Branding saved — ${filesUploaded === 1 ? '1 image' : '2 images'} uploaded`);
    } catch {
      // mutation onError shows the specific error toast
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveLocale = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: UpsertTenantSettingsPayload = {
      timezone,
      language,
      currency,
      dateFormat,
      timeFormat,
    };
    localeMutation.mutate(payload);
  };

  const handleSaveNumbering = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedCustomer = customerCodePrefix.trim();
    if (!trimmedCustomer) {
      toast.error('Customer Code prefix is required');
      return;
    }
    numberingMutation.mutate({
      customerCodePrefix: trimmedCustomer,
      assetNumberPrefix: assetNumberPrefix.trim(),
    });
  };

  if (profileLoading || settingsLoading) return <SkeletonPage />;

  const localeSaving = localeMutation.isPending;
  const numberingSaving = numberingMutation.isPending;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Company Settings"
        description="Manage your organisation's information, branding, and regional preferences"
      />

      {/* Each card below owns its own form. They are siblings, never nested — a
          form inside a form submits natively and reloads the page. */}
      <form
        onSubmit={(e) => {
          void handleSaveCompanyInfo(e);
        }}
      >
        <SectionCard
          title="Company Information"
          description="Legal and display details for your organisation"
          action={
            <SaveButton saving={savingSection === 'company'} label="Save" savingLabel="Saving…" />
          }
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {/* Tenant-owned since COMPANIES_DESIGN v1.1 — you declare your own
                legal entity; the Platform Console only displays it. */}
            <FormField label="Registered Name" htmlFor="cp-name">
              <Input
                id="cp-name"
                value={registeredName}
                maxLength={255}
                onChange={(e) => setRegisteredName(e.target.value)}
                placeholder="Full legal entity name as registered"
                disabled={isSavingAll}
              />
            </FormField>

            {/* Editable: Display / Trading Name */}
            <FormField label="Display / Trading Name" htmlFor="cp-title">
              <Input
                id="cp-title"
                value={companyTitle}
                onChange={(e) => setCompanyTitle(e.target.value)}
                placeholder="Acme Cooling"
                disabled={isSavingAll}
              />
            </FormField>

            {/* Tenant-owned since COMPANIES_DESIGN v1.1. */}
            <FormField label="Registration Number" htmlFor="cp-regno">
              <Input
                id="cp-regno"
                value={registrationNumber}
                maxLength={100}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                placeholder="Registration / incorporation identifier"
                disabled={isSavingAll}
              />
            </FormField>

            {/* Editable: Tax / VAT Number */}
            <FormField label="Tax / VAT Number" htmlFor="cp-tax">
              <Input
                id="cp-tax"
                value={taxNumber}
                onChange={(e) => setTaxNumber(e.target.value)}
                placeholder="27AAPCS1234A1Z5"
                disabled={isSavingAll}
              />
            </FormField>

            {/* Editable: Main Phone */}
            <FormField label="Main Phone" htmlFor="cp-phone">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="cp-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91-9876543210"
                  disabled={isSavingAll}
                  className="pl-9"
                />
              </div>
            </FormField>

            {/* Editable: Website */}
            <FormField label="Website" htmlFor="cp-website">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faGlobe}
                  className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="cp-website"
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://www.example.com"
                  disabled={isSavingAll}
                  className="pl-9"
                />
              </div>
            </FormField>

            {/* Editable: Working Hours — drives {{company_working_hours}} */}
            <FormField label="Working Hours" htmlFor="cp-working-hours">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faClock}
                  className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="cp-working-hours"
                  value={workingHours}
                  onChange={(e) => setWorkingHours(e.target.value)}
                  placeholder="Mon–Fri, 09:00–18:00"
                  disabled={isSavingAll}
                  className="pl-9"
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Used in emails as <code className="font-mono">{'{{company_working_hours}}'}</code>.
              </p>
            </FormField>
          </div>
        </SectionCard>
      </form>

      <form
        onSubmit={(e) => {
          void handleSaveSupportInfo(e);
        }}
      >
        <SectionCard
          title="Support Information"
          description="Contact details and address for customer and user support"
          action={
            <SaveButton saving={savingSection === 'support'} label="Save" savingLabel="Saving…" />
          }
        >
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <FormField label="Support Email" htmlFor="cp-support-email">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    id="cp-support-email"
                    type="email"
                    value={supportEmail}
                    onChange={(e) => setSupportEmail(e.target.value)}
                    placeholder="support@company.com"
                    disabled={isSavingAll}
                    className="pl-9"
                  />
                </div>
              </FormField>

              <FormField label="Support Phone" htmlFor="cp-support-phone">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faPhone}
                    className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    id="cp-support-phone"
                    type="tel"
                    value={supportPhone}
                    onChange={(e) => setSupportPhone(e.target.value)}
                    placeholder="+91-1800-000-0000"
                    disabled={isSavingAll}
                    className="pl-9"
                  />
                </div>
              </FormField>

              <FormField label="Support Working Hours" htmlFor="cp-support-hours">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    id="cp-support-hours"
                    value={supportHours}
                    onChange={(e) => setSupportHours(e.target.value)}
                    placeholder="Leave blank to use Working Hours"
                    disabled={isSavingAll}
                    className="pl-9"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Used in emails as <code className="font-mono">{'{{support_hours}}'}</code>. Falls
                  back to Working Hours when blank.
                </p>
              </FormField>

              <ComingSoonField label="Emergency Contact" placeholder="+91-9999999999" />
            </div>

            {/* Address */}
            <div className="border-t border-border pt-5">
              <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Office Address
              </p>
              <div className="space-y-4">
                <FormField label="Address Line 1" htmlFor="cp-addr1">
                  <Input
                    id="cp-addr1"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    placeholder="42, Industrial Estate"
                    disabled={isSavingAll}
                  />
                </FormField>
                <FormField label="Address Line 2" htmlFor="cp-addr2">
                  <Input
                    id="cp-addr2"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    placeholder="Near Railway Station"
                    disabled={isSavingAll}
                  />
                </FormField>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField label="City" htmlFor="cp-city">
                    <Input
                      id="cp-city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Mumbai"
                      disabled={isSavingAll}
                    />
                  </FormField>
                  <FormField label="State / Province" htmlFor="cp-state">
                    <Input
                      id="cp-state"
                      value={stateVal}
                      onChange={(e) => setStateVal(e.target.value)}
                      placeholder="Maharashtra"
                      disabled={isSavingAll}
                    />
                  </FormField>
                  <FormField label="Country" htmlFor="cp-country">
                    <Input
                      id="cp-country"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder="India"
                      disabled={isSavingAll}
                    />
                  </FormField>
                  <FormField label="Postal Code" htmlFor="cp-postal">
                    <Input
                      id="cp-postal"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="400001"
                      disabled={isSavingAll}
                    />
                  </FormField>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>
      </form>

      <form
        onSubmit={(e) => {
          void handleSaveClassification(e);
        }}
      >
        <SectionCard
          title="Business Classification"
          description="Select all that apply — used for reporting and workflow configuration only"
          action={
            <SaveButton
              saving={savingSection === 'classification'}
              label="Save"
              savingLabel="Saving…"
            />
          }
        >
          <div
            className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            role="group"
            aria-label="Business types"
          >
            {BUSINESS_TYPES.map(({ value, label }) => {
              const checked = selectedBusinessTypes.includes(value);
              return (
                <label
                  key={value}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-sm transition-colors ${
                    checked
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border text-foreground hover:bg-muted/40'
                  } ${isSavingAll ? 'pointer-events-none opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleBusinessType(value)}
                    disabled={isSavingAll}
                    className="h-4 w-4 shrink-0 accent-primary"
                    aria-label={label}
                  />
                  <span className="leading-snug">{label}</span>
                </label>
              );
            })}
          </div>
        </SectionCard>
      </form>

      {/* Branding — files are staged locally, then uploaded by this card's own Save. */}
      <form
        onSubmit={(e) => {
          void handleSaveBranding(e);
        }}
      >
        <SectionCard
          title="Branding"
          description="Choose a logo or favicon, then save this section to upload it"
          action={
            <SaveButton
              saving={savingSection === 'branding'}
              label="Save"
              savingLabel="Uploading…"
            />
          }
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <BrandingUploadField
              label="Company Logo"
              savedUrl={profile?.logoUrl ?? null}
              pendingPreviewUrl={pendingLogoPreview}
              hasPending={!!pendingLogoFile}
              onSelectFile={selectLogo}
              onClearPending={clearPendingLogo}
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              formats="Accepted: PNG, JPEG, WebP, SVG"
              dimensions="Recommended: 400 × 120 px — max 5 MB"
              previewClass="h-20 w-48"
              disabled={isSavingAll}
            />
            <BrandingUploadField
              label="Favicon"
              savedUrl={profile?.faviconUrl ?? null}
              pendingPreviewUrl={pendingFaviconPreview}
              hasPending={!!pendingFaviconFile}
              onSelectFile={selectFavicon}
              onClearPending={clearPendingFavicon}
              accept="image/png,image/x-icon,image/svg+xml"
              formats="Accepted: PNG, ICO, SVG"
              dimensions="Recommended: 32 × 32 px — max 2 MB"
              previewClass="h-16 w-16"
              disabled={isSavingAll}
            />
          </div>
        </SectionCard>
      </form>

      {/* Sits outside every form — it is page-level guidance, not a field. */}
      {!profile && !profileLoading && (
        <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
          <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 shrink-0" />
          <span>
            Your company profile has not been configured yet. Fill in the details above and save
            each section.
          </span>
        </div>
      )}

      {/* ── Regional Settings — separate form ───────────────────────── */}
      <form onSubmit={handleSaveLocale}>
        <SectionCard
          title="Regional Settings"
          description="Timezone, language, currency, and date/time display format for your workspace"
          action={<SaveButton saving={localeSaving} label="Save" savingLabel="Saving…" />}
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField label="Timezone" htmlFor="ts-timezone">
              <select
                id="ts-timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                disabled={localeSaving}
                className={SELECT_CLASS}
                aria-label="Timezone"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Language" htmlFor="ts-language">
              <select
                id="ts-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={localeSaving}
                className={SELECT_CLASS}
                aria-label="Language"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Currency" htmlFor="ts-currency">
              <select
                id="ts-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={localeSaving}
                className={SELECT_CLASS}
                aria-label="Currency"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Date Format" htmlFor="ts-date-format">
              <select
                id="ts-date-format"
                value={dateFormat}
                onChange={(e) => setDateFormat(e.target.value)}
                disabled={localeSaving}
                className={SELECT_CLASS}
                aria-label="Date format"
              >
                {DATE_FORMATS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </FormField>

            {/* Time Format — full-width radio pair */}
            <div className="sm:col-span-2">
              <p className="mb-2 text-sm font-medium text-foreground">Time Format</p>
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Time format">
                {(
                  [
                    { value: '24h', label: '24-hour', example: '14:30' },
                    { value: '12h', label: '12-hour', example: '2:30 PM' },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-sm transition-colors ${
                      timeFormat === opt.value
                        ? 'border-primary bg-primary/5 font-medium text-primary'
                        : 'border-border text-foreground hover:bg-muted/40'
                    } ${localeSaving ? 'pointer-events-none opacity-50' : ''}`}
                  >
                    <input
                      type="radio"
                      name="timeFormat"
                      value={opt.value}
                      checked={timeFormat === opt.value}
                      onChange={() => setTimeFormat(opt.value)}
                      disabled={localeSaving}
                      className="h-4 w-4 accent-primary"
                    />
                    <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      {opt.label}
                      <span className="ml-1.5 text-muted-foreground">({opt.example})</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>
      </form>

      {/* ── Record Numbering — Issue 8 ──────────────────────────────── */}
      <form onSubmit={handleSaveNumbering}>
        <SectionCard
          title="Record Numbering"
          description="Prefixes applied when generating identifiers for new records"
          action={<SaveButton saving={numberingSaving} label="Save" savingLabel="Saving…" />}
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField label="Customer Code Prefix" htmlFor="np-customer-code">
              <Input
                id="np-customer-code"
                value={customerCodePrefix}
                onChange={(e) => setCustomerCodePrefix(e.target.value)}
                disabled={numberingSaving}
                maxLength={10}
                placeholder="CUS"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Auto-generated for new customers, e.g.{' '}
                <code>{(customerCodePrefix || 'CUS').trim()}00001</code>. Existing customer codes
                are never changed.
              </p>
            </FormField>

            <FormField label="Asset Number Prefix" htmlFor="np-asset-number">
              <Input
                id="np-asset-number"
                value={assetNumberPrefix}
                onChange={(e) => setAssetNumberPrefix(e.target.value)}
                disabled={numberingSaving}
                maxLength={10}
                placeholder="e.g. AST-"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Optional. Pre-fills the Asset Number field during manual equipment registration only
                — you can still edit or clear it. Bulk-imported asset numbers are never altered.
              </p>
            </FormField>
          </div>
        </SectionCard>
      </form>

      {/* ── Inspection Report Numbering (Phase 4) ───────────────────────── */}
      <ReportNumberingSection />
    </div>
  );
}
